import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import { seasonActions } from '@redux/store/main/season.store'
import {
	ProductType,
	SeasonCreationType,
	SeasonModificationType,
	SeasonType,
} from '@schemas'
import { ResType } from '@api/types'
import { productApi, seasonApi } from '@api'
import { RootState } from '@redux/store'
import { Season } from '@schemas'

// Define the return type for the generator functions
function* apiGetSeason(year: string) {
	const res: ResType | undefined = yield call(seasonApi.getSeason, {
		query: { year },
	})
	return res
}

function* fetchProducts() {
	const response: ResType | undefined = yield call(productApi.getProducts)
	return response
}

// API call to delete a season
const deleteSeason = async (id: string): Promise<ResType | undefined> => {
	const response = await seasonApi.deleteSeason({ params: { seasonId: id } })
	return response
}

// API call to update a season
const updateSeason = async (body: {
	id: string
	season: SeasonModificationType
}): Promise<ResType | undefined> => {
	const { id, season } = body
	const response = await seasonApi.updateSeason({
		params: { seasonId: id },
		body: season,
	})
	return response
}

// New function to fetch and set season list
function* fetchAndSetSeasonList(): Generator<unknown, void, unknown> {
	const selectedYear = yield select(state => state.season.selectedYear)
	const apiRes = (yield call(apiGetSeason, selectedYear as string)) as ResType

	if (apiRes.status === 200) {
		const newSeason = apiRes.data.map(aData => new Season(aData as any))
		yield put(seasonActions.SEASON_LIST(newSeason))
	} else {
		yield put(seasonActions.SEASON_LIST([]))
	}
}

function* registerSeason(action: {
	type: string
	payload: SeasonCreationType
}): Generator<unknown, void, unknown> {
	try {
		yield call(seasonApi.createSeason, { body: action.payload })
		yield call(fetchAndSetSeasonList)
		yield put(seasonActions.REGISTER_SEASON_SUCCESS())
	} catch (error: any) {
		yield put(seasonActions.REGISTER_SEASON_FAILURE(error.message))
	}
}

function* deleteSeasonSaga(action: {
	type: string
	payload: string
}): Generator<unknown, void, unknown> {
	try {
		yield call(deleteSeason, action.payload)
		yield call(fetchAndSetSeasonList)
		yield put(seasonActions.DELETE_SEASON_SUCCESS(action.payload))
	} catch (error: any) {
		yield put(seasonActions.DELETE_SEASON_FAILURE(error.message))
	}
}

function* updateSeasonSaga(action: {
	type: string
	payload: { id: string; season: SeasonModificationType }
}): Generator<unknown, void, unknown> {
	try {
		yield call(updateSeason, action.payload)
		yield call(fetchAndSetSeasonList)
		yield put(seasonActions.UPDATE_SEASON_SUCCESS())
	} catch (error: any) {
		yield put(seasonActions.UPDATE_SEASON_FAILURE(error.message))
	}
}

function* fetchAndSetProductList(): Generator<unknown, void, unknown> {
	const response: ResType | undefined = (yield call(fetchProducts)) as ResType
	yield put(seasonActions.SET_PRODUCT_LIST((response?.data || []) as ProductType[]))
}

// 페이지 초기화 관련
function* triggerInitSeasonPage(): Generator<unknown, void, unknown> {
	try {
		yield call(fetchAndSetSeasonList)
		yield call(fetchAndSetProductList)
		yield put(seasonActions.TRIGGER_INIT_SEASON_PAGE_SUCCESS())
	} catch (error: any) {
		yield put(seasonActions.TRIGGER_INIT_SEASON_PAGE_FAILURE(error.message))
	}
}

function* fetchSeasonOptions(): Generator<unknown, void, unknown> {
	yield call(fetchAndSetSeasonList)
}

// watch saga
export function* watchFetchSeasonOptionsSaga(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.SET_SELECTED_YEAR.type, fetchSeasonOptions)
}

export function* watchTriggerInitSeasonPage(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.TRIGGER_INIT_SEASON_PAGE.type, triggerInitSeasonPage)
}

export function* watchDeleteSeasonSaga(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.DELETE_SEASON.type, deleteSeasonSaga)
}

export function* watchRegisterSeasonSaga(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.REGISTER_SEASON.type, registerSeason)
}

export function* watchUpdateSeasonSaga(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.UPDATE_SEASON.type, updateSeasonSaga)
}

// saga
export default function* seasonSaga(): Generator<unknown, void, unknown> {
	yield all([
		watchRegisterSeasonSaga(),
		watchUpdateSeasonSaga(),
		watchDeleteSeasonSaga(),
		watchTriggerInitSeasonPage(),
		watchFetchSeasonOptionsSaga(),
	])
}
